import { useCallback, useEffect, useState } from "react";
import { MapPinOff, Wand2, CheckCheck, ExternalLink } from "lucide-react";
import { http, toastApiError } from "../../lib/api";
import { toast } from "../../lib/notify";
import { cityKey, suggestCity, districtKey, suggestDistrict } from "../../lib/trCity";

// il / ilçe adı resmi listede olmayan şubeler → önerilen değerle tek tıkla düzelt (BALIKKESİR → BALIKESİR, ZEYTİNBURNİ → ZEYTİNBURNU)
const analyze = (l) => {
  if (l.is_correction) return null;   // düzeltme farkları şubesinde il/ilçe tutulmaz
  const rawCity = (l.city || "").trim(), rawDist = (l.district || "").trim();
  const cityOk = !!cityKey(rawCity);
  const city = cityOk ? cityKey(rawCity) : suggestCity(rawCity);
  const distOk = !!city && !!rawDist && !!districtKey(city, rawDist);
  const district = distOk ? districtKey(city, rawDist) : (city && rawDist ? suggestDistrict(city, rawDist) : "");
  const cityIssue = !!rawCity && !cityOk, distIssue = !!rawDist && !!city && !distOk;
  if (!cityIssue && !distIssue) return null;
  const fixable = (!cityIssue || !!city) && (!distIssue || !!district);
  return { ...l, cityIssue, distIssue, cityFix: cityIssue ? city : "", distFix: distIssue ? district : "", fixable };
};

const Val = ({ children, bad }) => <span className={`px-1.5 py-0.5 rounded border font-mono ${bad ? "bg-white border-rose-300 line-through" : "bg-emerald-100 border-emerald-300 text-emerald-900 font-bold"}`}>{children}</span>;

export default function CityFixBanner({ projects, onFixed, onOpen, refreshKey }) {
  const [issues, setIssues] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => http.get("/locations").then((r) => setIssues(r.data.map(analyze).filter(Boolean))).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!issues.length) return null;
  const pname = (id) => projects.find((p) => p.id === id)?.name || "";
  const body = (l) => ({ ...l, city: l.cityFix || l.city, district: l.distFix || l.district });
  const fix = async (l) => {
    setBusy(l.id);
    try { await http.put(`/locations/${l.id}`, body(l)); toast.success(`${l.name}: ${[l.cityFix && `${l.city} → ${l.cityFix}`, l.distFix && `${l.district} → ${l.distFix}`].filter(Boolean).join(", ")}`); await load(); onFixed?.(l.project_id); }
    catch (e) { toastApiError(e); } finally { setBusy(""); }
  };
  const fixAll = async () => {
    setBusy("all");
    const ok = [];
    for (const l of issues.filter((x) => x.fixable)) { try { await http.put(`/locations/${l.id}`, body(l)); ok.push(l); } catch (e) { toastApiError(e); } }
    toast.success(`${ok.length} şubenin il/ilçe adı düzeltildi`);
    await load(); ok.forEach((l) => onFixed?.(l.project_id)); setBusy("");
  };
  const nFix = issues.filter((x) => x.fixable).length;
  return (
    <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-xs text-rose-900" data-testid="city-fix-banner">
      <div className="flex items-center gap-2 flex-wrap">
        <MapPinOff className="w-4 h-4 text-rose-700 shrink-0" />
        <span className="font-bold uppercase">İl / ilçe adı düzeltilmeli:</span>
        <span data-testid="city-fix-count">{issues.length} şubenin il veya ilçe adı resmi listede yok — geçici personel havuzu ve il bazlı raporlar bu şubeleri eşleştiremez.</span>
        {nFix > 0 && <button type="button" onClick={fixAll} disabled={!!busy} data-testid="city-fix-all" className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-700 text-white font-bold hover:bg-rose-800 disabled:opacity-50"><CheckCheck className="w-3.5 h-3.5" /> Tümünü Düzelt ({nFix})</button>}
      </div>
      <ul className="mt-2 space-y-1">
        {issues.map((l) => (
          <li key={l.id} className="flex items-center gap-2 flex-wrap" data-testid={`city-fix-row-${l.id}`}>
            <span className="font-semibold">{l.name}</span><span className="text-rose-700">({pname(l.project_id)})</span>
            {l.cityIssue && <><span className="text-slate-500">il:</span><Val bad>{l.city}</Val><span>→</span>{l.cityFix ? <span data-testid={`city-fix-suggestion-${l.id}`}><Val>{l.cityFix}</Val></span> : <span className="italic text-slate-500">öneri yok</span>}</>}
            {l.distIssue && <><span className="text-slate-500">ilçe:</span><Val bad>{l.district}</Val><span>→</span>{l.distFix ? <span data-testid={`district-fix-suggestion-${l.id}`}><Val>{l.distFix}</Val></span> : <span className="italic text-slate-500">öneri yok</span>}</>}
            {l.fixable && <button type="button" onClick={() => fix(l)} disabled={!!busy} data-testid={`city-fix-${l.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-rose-400 bg-white font-bold hover:bg-rose-100 disabled:opacity-50"><Wand2 className="w-3 h-3" /> Düzelt</button>}
            <button type="button" onClick={() => onOpen?.(l)} disabled={!!busy} data-testid={`city-fix-open-${l.id}`} title="Şubeyi düzenleme ekranında aç — il/ilçeyi listeden seçip kaydedince uyarı kalkar" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-700 text-white font-bold hover:bg-rose-800 disabled:opacity-50"><ExternalLink className="w-3 h-3" /> Hataya Git</button>
            {!l.fixable && <span className="italic text-slate-500">(öneri yok — şube formunda il/ilçeyi listeden seçin)</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
